import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  Chip,
  Switch,
  FormControlLabel,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Stack,
  Alert,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Icon from '@mdi/react';
import { getIconPath } from '../../utils/mdiIcons';
import { TabbedTranslatableFields } from '../PropertyEditor/common/TabbedTranslatableFields';
import IconField from '../PropertyEditor/common/IconField';
import { useEditor } from '../../context/EditorContext';
import { useFeedback } from '../../context/FeedbackContext';
import { Module, ListingFlow } from '../../models/listingFlow';
import { danglingModuleIds } from '../../utils/moduleValidation';
import { extractModuleArtifact, mergeModuleArtifact } from '../../utils/moduleArtifact';
import { transformFlowForExport } from '../../utils/uuidUtils';

interface ModuleManagerDialogProps {
  open: boolean;
  onClose: () => void;
}

const SLUG_PATTERN = /^[a-z0-9_]+$/;

// Erklärtexte (Tooltips) — fassen das mentale Modell zusammen
const MODULE_INFO =
  'Module sind optionale, pro Projekt zuschaltbare Bausteine. Hier definierst du sie und ordnest ' +
  'Seiten/Elemente per „Modul-Zuordnung" zu. Ob ein Modul in einem konkreten Projekt aktiv ist ' +
  '(Feld module_<id>_active), entscheidet der Energieberater zur Laufzeit in der doorbit-App — nicht hier. ' +
  'Dieser Editor legt nur die Vorlage und den Standard fest (siehe „Bei Projektanlage vorausgewählt").';

const ARTIFACT_INFO =
  'CATALOG-Modul-Artefakt = der Modul-Inhalt als eigenständige, flow-förmige JSON-Datei ' +
  '(modul-getaggte Seiten + Modul-Eintrag + Version). „Exportieren" erzeugt diese Datei, die portal über ' +
  'flowModule(id) nachlädt und versioniert offline cacht; „Importieren" führt so eine Datei in den aktuellen ' +
  'Flow ein. Nur für CATALOG relevant — INLINE-Module stecken im Basis-Flow und brauchen kein Artefakt.';

const DELIVERY_INFO =
  'INLINE: Modul steckt direkt im Basis-Flow, sofort einsatzbereit. ' +
  'CATALOG: Modul wird als separates, versioniertes Artefakt ausgeliefert und von portal bei Bedarf nachgeladen.';

const DEFAULT_ACTIVE_INFO =
  'Nur der Standard bei Projektanlage. Das tatsächliche An/Aus pro Projekt setzt der Energieberater ' +
  'zur Laufzeit in der doorbit-App (module_<id>_active) — nicht in diesem Editor.';

const InfoTip: React.FC<{ text: string }> = ({ text }) => (
  <Tooltip title={text}>
    <InfoOutlinedIcon fontSize="small" sx={{ color: 'action.active', cursor: 'help' }} />
  </Tooltip>
);

const emptyModule = (): Module => ({
  id: '',
  name: { de: '', en: '' },
  description: { de: '', en: '' },
  icon: '',
  default_active: false,
  delivery: 'INLINE',
});

/**
 * Verwaltet den Modul-Katalog des aktuellen Flows (ListingFlow.modules):
 * Module anlegen, bearbeiten und löschen. Module sind optional pro Projekt aktivierbar
 * (Aktivierungsfeld `module_<id>_active`); mit `module_id` getaggte Seiten/Elemente
 * werden nur sichtbar, wenn ihr Modul aktiv ist.
 */
const ModuleManagerDialog: React.FC<ModuleManagerDialogProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { state, dispatch } = useEditor();
  const { showSuccess, showError, confirm } = useFeedback();

  const modules = state.currentFlow?.modules ?? [];
  const dangling = danglingModuleIds(state.currentFlow);

  // Editor-Sub-Dialog (Anlegen/Bearbeiten)
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editModule, setEditModule] = useState<Module>(emptyModule());
  const [languageTab, setLanguageTab] = useState(0);


  const handleOpenAdd = () => {
    setEditingId(null);
    setEditModule(emptyModule());
    setLanguageTab(0);
    setEditorOpen(true);
  };

  const handleOpenEdit = (module: Module) => {
    setEditingId(module.id);
    setEditModule({ ...module });
    setLanguageTab(0);
    setEditorOpen(true);
  };

  const idError = (() => {
    const id = editModule.id.trim();
    if (!id) return 'ID erforderlich';
    if (!SLUG_PATTERN.test(id)) return 'Nur Kleinbuchstaben, Ziffern und Unterstrich';
    if (modules.some(m => m.id === id && m.id !== editingId)) return 'ID bereits vergeben';
    return null;
  })();

  const handleSaveModule = () => {
    if (idError) return;
    const moduleToSave: Module = { ...editModule, id: editModule.id.trim() };
    if (editingId === null) {
      dispatch({ type: 'ADD_MODULE', module: moduleToSave });
    } else {
      dispatch({ type: 'UPDATE_MODULE', moduleId: editingId, updates: moduleToSave });
    }
    setEditorOpen(false);
    showSuccess(`Modul „${moduleToSave.id}" gespeichert.`);
  };

  const handleDeleteModule = async (moduleId: string) => {
    const ok = await confirm({
      title: 'Modul löschen?',
      message: 'Das Modul wird aus dem Katalog entfernt. Bestehende Zuordnungen (module_id) an Seiten und Elementen werden dabei gelöst — die betroffenen Inhalte bleiben erhalten und sind wieder modul-unabhängig sichtbar.',
      confirmLabel: 'Löschen',
      destructive: true,
    });
    if (ok) {
      dispatch({ type: 'REMOVE_MODULE', moduleId });
      showSuccess(`Modul „${moduleId}" gelöscht.`);
    }
  };

  // Exportiert ein Modul als eigenständiges CATALOG-Artefakt (flow-förmige JSON).
  const handleExportArtifact = (moduleId: string) => {
    const artifact = extractModuleArtifact(state.currentFlow, moduleId);
    if (!artifact) {
      showError(`Modul „${moduleId}" konnte nicht als Artefakt exportiert werden.`);
      return;
    }
    const json = JSON.stringify(transformFlowForExport(artifact), null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${moduleId}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    showSuccess(`Modul „${moduleId}" als Artefakt exportiert.`);
  };

  // Importiert ein Modul-Artefakt und führt es in den aktuellen Flow zusammen.
  const handleImportArtifact = () => {
    if (!state.currentFlow) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json,.json';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const artifact = JSON.parse(event.target?.result as string) as ListingFlow;
          if (!artifact || !Array.isArray(artifact.modules) || artifact.modules.length === 0) {
            throw new Error('Datei enthält keinen Modul-Katalog (modules[]).');
          }
          const merged = mergeModuleArtifact(state.currentFlow!, artifact);
          dispatch({ type: 'UPDATE_FLOW', flow: merged });
          showSuccess(`Modul-Artefakt „${artifact.modules[0].id}" importiert.`);
        } catch (err) {
          showError('Artefakt konnte nicht importiert werden: ' + (err instanceof Error ? err.message : 'ungültige Datei'));
        }
      };
      reader.readAsText(file);
    };
    input.click();
  };

  const renderModuleIcon = (iconName?: string) => {
    if (!iconName || !iconName.startsWith('mdi')) return null;
    const path = getIconPath(iconName);
    return path ? <Icon path={path} size={1} /> : null;
  };

  const isCatalog = editModule.delivery === 'CATALOG';

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={fullScreen}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Module verwalten
            <InfoTip text={MODULE_INFO} />
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Module sind optionale, pro Projekt aktivierbare Bausteine. Seiten und Elemente werden
            später per Modul-Zuordnung einem Modul zugewiesen und sind nur sichtbar, wenn das Projekt
            das Modul aktiviert hat.
          </Typography>

          {dangling.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {dangling.length === 1
                ? 'Eine Modul-Zuordnung verweist auf ein nicht (mehr) vorhandenes Modul: '
                : 'Mehrere Modul-Zuordnungen verweisen auf nicht (mehr) vorhandene Module: '}
              <strong>{dangling.join(', ')}</strong>. Lege die fehlenden Module an oder entferne die
              Zuordnung an den betroffenen Seiten/Elementen.
            </Alert>
          )}

          {modules.length === 0 ? (
            <Box
              sx={{
                py: 4,
                textAlign: 'center',
                color: 'text.secondary',
                border: '1px dashed',
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <Typography variant="body2">Noch keine Module angelegt.</Typography>
            </Box>
          ) : (
            <Stack spacing={1}>
              {modules.map(module => (
                <Card key={module.id} variant="outlined">
                  <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                        {renderModuleIcon(module.icon)}
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="subtitle2" noWrap>
                            {module.name?.de || module.id}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {module.id}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                        <Chip
                          size="small"
                          label={module.delivery || 'INLINE'}
                          color={module.delivery === 'CATALOG' ? 'info' : 'default'}
                          variant="outlined"
                        />
                        {module.default_active && (
                          <Chip size="small" label="Standard aktiv" color="success" variant="outlined" />
                        )}
                        <IconButton size="small" onClick={() => handleOpenEdit(module)} aria-label="Bearbeiten">
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <Tooltip title={ARTIFACT_INFO}>
                          <IconButton
                            size="small"
                            onClick={() => handleExportArtifact(module.id)}
                            aria-label="Als CATALOG-Artefakt exportieren"
                          >
                            <FileDownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteModule(module.id)}
                          aria-label="Löschen"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
            <Button startIcon={<AddIcon />} onClick={handleOpenAdd}>
              Modul hinzufügen
            </Button>
            <Tooltip title={ARTIFACT_INFO}>
              <Button startIcon={<FileUploadIcon />} onClick={handleImportArtifact}>
                Artefakt importieren
              </Button>
            </Tooltip>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Schließen</Button>
        </DialogActions>
      </Dialog>

      {/* Sub-Dialog: Modul anlegen / bearbeiten */}
      <Dialog
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={fullScreen}
      >
        <DialogTitle>{editingId === null ? 'Neues Modul' : 'Modul bearbeiten'}</DialogTitle>
        <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Modul-ID"
              size="small"
              fullWidth
              value={editModule.id}
              onChange={e => setEditModule({ ...editModule, id: e.target.value })}
              error={!!idError}
              helperText={idError || `Aktivierungsfeld: module_${editModule.id || '<id>'}_active`}
            />
            {editingId !== null && (
              <Typography variant="caption" color="warning.main">
                Achtung: Die ID ist der Schlüssel des Moduls. Beim Ändern werden bestehende
                Zuordnungen (module_id) automatisch mit-migriert.
              </Typography>
            )}

            <TabbedTranslatableFields
              fields={[
                {
                  id: 'name',
                  label: 'Name',
                  value: editModule.name,
                  onChange: value => setEditModule({ ...editModule, name: value }),
                },
                {
                  id: 'description',
                  label: 'Beschreibung',
                  value: editModule.description,
                  onChange: value => setEditModule({ ...editModule, description: value }),
                  multiline: true,
                  rows: 2,
                },
              ]}
              languageTab={languageTab}
              onLanguageTabChange={setLanguageTab}
            />

            <IconField
              value={editModule.icon || ''}
              onChange={value => setEditModule({ ...editModule, icon: value })}
              label="Icon"
              fullWidth
            />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!editModule.default_active}
                    onChange={e => setEditModule({ ...editModule, default_active: e.target.checked })}
                  />
                }
                label="Bei Projektanlage vorausgewählt"
              />
              <InfoTip text={DEFAULT_ACTIVE_INFO} />
            </Box>

            <Divider />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FormControl size="small" fullWidth>
                <InputLabel id="module-delivery-label">Auslieferung</InputLabel>
                <Select
                  labelId="module-delivery-label"
                  label="Auslieferung"
                  value={editModule.delivery || 'INLINE'}
                  onChange={e =>
                    setEditModule({ ...editModule, delivery: e.target.value as 'INLINE' | 'CATALOG' })
                  }
                >
                  <MenuItem value="INLINE">INLINE (im Flow enthalten)</MenuItem>
                  <MenuItem value="CATALOG">CATALOG (separat nachladbar)</MenuItem>
                </Select>
              </FormControl>
              <InfoTip text={DELIVERY_INFO} />
            </Box>

            {isCatalog && (
              <TextField
                label="Version"
                size="small"
                fullWidth
                value={editModule.version || ''}
                onChange={e => setEditModule({ ...editModule, version: e.target.value })}
                placeholder="z. B. 1.0.0"
                helperText="Semver-artige Version des CATALOG-Modul-Artefakts"
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditorOpen(false)}>Abbrechen</Button>
          <Button onClick={handleSaveModule} variant="contained" disabled={!!idError}>
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

    </>
  );
};

export default ModuleManagerDialog;
